import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { ShiftOptions, ShiftStatuses } from 'models/ICattlesData'
import cattlesApiServices from 'services/cattlesApiServices'
import { useAppSelector } from 'store/hooks'

export interface ShiftsResponse {
	payload: {
		day_period: ShiftOptions
		status: ShiftStatuses
	}[]
}
interface Shift {
	id: number
	title: string
	description: string
	disabled?: boolean
	value: ShiftOptions
	isAvailableInParlor?: boolean
}
export interface DailyShiftProps {
	date?: number
	cameraId?: number
	includeUnavailable?: boolean
	enabled?: boolean
}

export const useGetDailyShifts = ({
	date,
	cameraId,
	includeUnavailable = false,
	enabled = true,
}: DailyShiftProps) => {
	const { userData } = useAppSelector(state => state.userReducer)
	const availableShifts = useQuery<ShiftsResponse, AxiosError>({
		queryKey: ['daily-shifts', date, cameraId],
		queryFn: () => cattlesApiServices.getDailyShifts({ date, cameraId }),
		staleTime: 50000,
		retry: 1,
		enabled,
	})

	const shiftStatusMap = new Map(
		availableShifts.data?.payload.map(({ day_period, status }) => [
			day_period,
			status,
		]),
	)

	const shouldUseShift = (shift: ShiftOptions) =>
		shiftStatusMap.has(shift) || includeUnavailable
	const getIsDisabled = (shift: ShiftOptions) => {
		const status = shiftStatusMap.get(shift)
		if (!status) return true
		return ['pending', 'closed'].includes(status)
	}

	const enrichedShifts: Shift[] = []

	if (
		userData.company.am_period_start &&
		userData.company.am_period_end &&
		shouldUseShift(ShiftOptions.AM)
	) {
		enrichedShifts.push({
			id: 0,
			title: 'Утренняя смена',
			value: ShiftOptions.AM,
			description: `${userData.company.am_period_start.slice(
				0,
				5,
			)} - ${userData.company.am_period_end.slice(0, 5)}`,
			disabled: getIsDisabled(ShiftOptions.AM),
			isAvailableInParlor: shiftStatusMap.has(ShiftOptions.AM),
		})
	}

	if (
		userData.company.pm_period_start &&
		userData.company.pm_period_end &&
		shouldUseShift(ShiftOptions.PM)
	) {
		enrichedShifts.push({
			id: 1,
			title: 'Дневная смена',
			value: ShiftOptions.PM,
			description: `${userData.company.pm_period_start.slice(
				0,
				5,
			)} - ${userData.company.pm_period_end.slice(0, 5)}`,
			disabled: getIsDisabled(ShiftOptions.PM),
			isAvailableInParlor: shiftStatusMap.has(ShiftOptions.PM),
		})
	}

	if (
		userData.company.night_period_start &&
		userData.company.night_period_end &&
		shouldUseShift(ShiftOptions.NIGHT)
	) {
		enrichedShifts.push({
			id: 2,
			title: 'Ночная смена',
			value: ShiftOptions.NIGHT,
			description: `${userData.company.night_period_start.slice(
				0,
				5,
			)} - ${userData.company.night_period_end.slice(0, 5)}`,
			disabled: getIsDisabled(ShiftOptions.NIGHT),
			isAvailableInParlor: shiftStatusMap.has(ShiftOptions.NIGHT),
		})
	}

	return {
		...availableShifts,
		data: enrichedShifts,
	}
}
